import React from 'react';
import { ArrowRight } from 'lucide-react';
import ApiTrialEndedNotice from '@/app/components/ApiTrialEndedNotice';

/**
 * The home page's view of an owner's sites whose partner API trial has ended
 * (backend #2236, `GET /api/sites/api_trial_ended`).
 *
 * `/api/sites` lists controlled sites only, so without this an owner whose
 * sites all ended their trial lands on the new-customer funnel ("Start the
 * survey") — the opposite of what is true. This shows the backend's notice
 * once, verbatim, and still links to each site's page.
 */
export type TrialEndedSite = { id: number; name: string; display_name?: string | null; message: string };

/** Tolerant fetch: a missing endpoint or error means "none", never a broken page. */
export async function fetchTrialEndedSites(): Promise<TrialEndedSite[]> {
  try {
    const res = await fetch('/api/sites/api_trial_ended');
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data.filter(s => s && typeof s.id === 'number' && typeof s.message === 'string') : [];
  } catch {
    return [];
  }
}

const TrialEndedSites: React.FC<{ sites: TrialEndedSite[] }> = ({ sites }) => {
  if (sites.length === 0) return null;
  return (
    <div className="space-y-3">
      <ApiTrialEndedNotice notice={{ state: 'api_trial_ended', message: sites[0].message }} />
      <div className="grid gap-2">
        {sites.map(site => (
          <a
            key={site.id}
            href={`/summary/${site.id}`}
            className="group flex items-center justify-between p-4 bg-card border border-border rounded-sm hover:border-foreground/30 transition-colors"
          >
            <p className="text-base font-medium text-foreground truncate">{site.display_name || site.name}</p>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default TrialEndedSites;
